import { Fragment, KeyboardEvent, useContext, useRef } from 'react'
import { observer } from 'mobx-react'
import { Icon } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import type { SelectOptionConfig } from '@/types/dataCollection'
import { resolveLabel, toIconType } from './helpers'

interface ChoiceCardGroupProps {
  options: SelectOptionConfig[]
  value: string | undefined
  onChange: (value: string) => void
  ariaLabel?: string
}

export const ChoiceCardGroup = observer(
  ({ options, value, onChange, ariaLabel }: ChoiceCardGroupProps) => {
    const { language } = useContext(StoreContext).TranslationsState
    const selectedIndex = options.findIndex((o) => o.value === value)
    // Group headings sit between the tiles, so the tiles are tracked by ref, not by DOM position.
    const tileRefs = useRef<(HTMLElement | null)[]>([])

    const move = (event: KeyboardEvent, delta: number) => {
      event.preventDefault()
      const from = selectedIndex >= 0 ? selectedIndex : 0
      const next = (from + delta + options.length) % options.length
      onChange(options[next].value)
      tileRefs.current[next]?.focus()
    }

    const onKeyDown = (event: KeyboardEvent, option: SelectOptionConfig) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') move(event, 1)
      else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') move(event, -1)
      else if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault()
        onChange(option.value)
      }
    }

    return (
      <div className="data-collection__cards" role="radiogroup" aria-label={ariaLabel}>
        {options.map((option, index) => {
          const selected = option.value === value
          const tabbable = selected || (selectedIndex < 0 && index === 0)
          const iconType = toIconType(option.icon)
          const heading = resolveLabel(option.heading, language)
          return (
            <Fragment key={option.value}>
              {heading && <span className="data-collection__cards-heading">{heading}</span>}
              <div
                ref={(element) => {
                  tileRefs.current[index] = element
                }}
                className={cx('generic__document-picker', selected && 'generic__current')}
                role="radio"
                aria-checked={selected}
                tabIndex={tabbable ? 0 : -1}
                onClick={() => onChange(option.value)}
                onKeyDown={(event) => onKeyDown(event, option)}
              >
                {iconType && <Icon type={iconType} size="32px" />}
                <div className="generic__document-picker-content">
                  <div className="generic__document-picker-head">
                    <p className="generic__document-picker-title">
                      {resolveLabel(option.label, language)}
                    </p>
                    {option.badge && (
                      <div className="generic__document-picker__badge">
                        {resolveLabel(option.badge, language)}
                      </div>
                    )}
                  </div>
                  {option.descriptionItems && option.descriptionItems.length > 0 && (
                    <ul className="generic__document-picker-items">
                      {option.descriptionItems.map((item, i) => (
                        <li key={i}>{resolveLabel(item, language)}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="generic__document-checked">
                  <Icon
                    type="selected-document"
                    size="32px"
                    color="var(--symbol-current, black)"
                    visible={selected}
                  />
                </div>
              </div>
            </Fragment>
          )
        })}
      </div>
    )
  }
)
